<script setup lang="ts">
import { RouterLink, RouterView, useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { OSidebar } from '@ai/ui-components'
import { STORAGE_KEYS } from '@/enums/StorageEnum'
import { useToast } from '@ai/ui-components/toast'
import { useAlerts } from '@/composables/alert/useAlerts.ts'
import { useNavigationPermissions } from '@/composables/permissions/useNavigationPermissions'
import { useBreadcrumbs } from '@/composables/navigation/useBreadcrumbs'
import { useConfigurator } from '@/composables/configurator/useConfigurator'
import AppToastContainer from '@/components/AppToastContainer.vue'

import { SIDEBAR_CONFIG } from '@/config/sidebar'
import logoUrl from '@/assets/img/svg/logo.svg?url'

const { t, locale: i18nLocale } = useI18n({ useScope: 'global' })
const route = useRoute()
const toast = useToast()
const { getFilteredConfig } = useNavigationPermissions()
const { hasPendingChanges, requestCancelNavigation } = useConfigurator()
const router = useRouter()

const handleBreadcrumbClick = (item: { to?: RouteLocationRaw }, _index: number, _event: Event) => {
  if (item.to) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (route.path.includes('/configurator') && hasPendingChanges) {
      const path = typeof item.to === 'string' ? item.to : JSON.stringify(item.to)
      requestCancelNavigation(path)
      return
    }
    void router.push(item.to)
  }
}

// Get filtered sidebar configuration based on permissions
const filteredSidebarConfig = getFilteredConfig(SIDEBAR_CONFIG)

// Get breadcrumbs based on current route and sidebar config
const { breadcrumbs } = useBreadcrumbs(filteredSidebarConfig)

const shouldScroll = computed(() => {
  return route.name === 'live-supervision'
})

watch(
  () => route.params.locale as string | undefined,
  (urlLocale) => {
    if (urlLocale) i18nLocale.value = urlLocale
  },
  { immediate: true },
)

const { alerts, deleteAlert } = useAlerts()

watch(
  alerts,
  (newAlerts) => {
    newAlerts.forEach((alert, index) => {
      if (alert.type === 'danger')
        toast.error(alert.options?.items ? '' : t('please_try_again'), {
          title: t(alert.message),
          items: alert.options?.items?.map((item) => ({ ...item, label: t(item.label) })),
        })
      else if (alert.type === 'only_message') toast.error(t(alert.message))
      else if (alert.type === 'success') toast.success(t(alert.message))
      else toast.info(alert.message)

      deleteAlert(index)
    })
  },
  { deep: true },
)
</script>

<template>
  <div class="flex h-screen min-h-screen w-full bg-gray-50">
    <OSidebar
      :config="filteredSidebarConfig"
      :local-storage-key="STORAGE_KEYS.SIDEBAR_OPEN_GROUPS"
      :link-component="RouterLink"
      :current-path="route.path"
    >
      <template #header>
        <img :src="logoUrl" alt="Logo" width="232" height="22" />
      </template>
    </OSidebar>

    <AppToastContainer />

    <main class="relative flex flex-1 flex-col overflow-hidden">
      <OBreadcrumbs
        v-if="breadcrumbs.length"
        :items="breadcrumbs"
        @click="handleBreadcrumbClick"
        class="pl-10 pt-4"
      />

      <div class="flex-1" :class="shouldScroll ? 'overflow-auto' : 'overflow-hidden'">
        <RouterView />
      </div>
    </main>
  </div>
</template>
